import { useCallback, useMemo } from 'react';

import { UPDATE_STATE } from '../actions';
import { filterUnlockedAnnotationIds } from '../utils/annotationLockUtils';
import dispatchTextWithCardSync from '../utils/dispatchTextWithCardSync';
import { hasTemplateCardBackground } from '../utils/textTemplateCardLayout';
import { TOOLS_IDS } from '../utils/constants';
import fitTextAnnotationToContent from '../utils/fitTextAnnotationToContent';
import useStore from './useStore';

const useFitTextToContent = (annotationIds) => {
  const { annotations = {}, designLayer, dispatch } = useStore();

  const targetIds = useMemo(() => {
    const ids = annotationIds?.length
      ? annotationIds
      : [];
    return filterUnlockedAnnotationIds(annotations, ids).filter(
      (id) => annotations[id]?.name === TOOLS_IDS.TEXT,
    );
  }, [annotationIds, annotations]);

  const canFitTextToContent = targetIds.length > 0;

  const fitTextToContent = useCallback(() => {
    if (targetIds.length === 0) {
      return;
    }

    const cardLinkedIds = targetIds.filter((id) =>
      hasTemplateCardBackground(annotations, id),
    );
    const plainIds = targetIds.filter(
      (id) => !hasTemplateCardBackground(annotations, id),
    );

    cardLinkedIds.forEach((id) => {
      const fitted = fitTextAnnotationToContent(annotations[id]);
      if (fitted) {
        dispatchTextWithCardSync(dispatch, annotations, fitted, designLayer);
      }
    });

    const plainPatches = {};
    plainIds.forEach((id) => {
      const fitted = fitTextAnnotationToContent(annotations[id]);
      if (fitted) {
        plainPatches[id] = fitted;
      }
    });

    const plainPatchIds = Object.keys(plainPatches);
    if (plainPatchIds.length === 0) {
      return;
    }

    if (plainPatchIds.length === 1) {
      dispatch({
        type: UPDATE_STATE,
        payload: (state) => ({
          isDesignState: true,
          annotations: {
            [plainPatchIds[0]]: {
              ...state.annotations[plainPatchIds[0]],
              ...plainPatches[plainPatchIds[0]],
            },
          },
        }),
      });
      return;
    }

    dispatch({
      type: UPDATE_STATE,
      payload: (state) => ({
        isDesignState: true,
        annotations: plainPatches,
      }),
    });
  }, [annotations, designLayer, dispatch, targetIds]);

  return { fitTextToContent, canFitTextToContent };
};

export default useFitTextToContent;
